import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { billingMessages } from 'src/features/billing/utils/messages';
import { LIST_BILLING_ENTITIES_RESOURCE } from 'src/features/billing/api/useListBillingEntities';
import { useDeleteBillingEntity } from 'src/features/billing/api/useDeleteBillingEntity';
import { BillingEntity } from 'src/features/billing/utils/types';
import { toast } from 'src/hooks/useToast';
import { useNavigate } from 'react-router-dom';

interface DeleteBillingEntityFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedBillingEntity: BillingEntity;
    children?: React.ReactNode;
}

function DeleteBillingEntityFormModal<TData>({
    selectedBillingEntity,
    children,
}: DeleteBillingEntityFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const navigate = useNavigate();

    const { mutate: deletePayStation } = useDeleteBillingEntity({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_BILLING_ENTITIES_RESOURCE] });
            setIsModalOpen(false);
            navigate(`/billing-entity`);
            toast({
                title: formatMessage(billingMessages.deleteBillingEntitySuccess),
                description: formatMessage(billingMessages.deleteBillingEntitySuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...billingMessages.deleteBillingEntity },
                subTitle: {
                    ...billingMessages.deleteBillingEntitySubtitle,
                    values: {
                        entity: selectedBillingEntity.name,
                    },
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...billingMessages.confirmDeleteBillingEntity },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: billingMessages.deleteBillingEntity,
                callbackFn: () => {
                    return (
                        deletePayStation({
                            id: selectedBillingEntity.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteBillingEntityFormModal;
